import React, { useState, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaCarouselType } from 'embla-carousel';

import NavButton from './subcomponents/NavButton';

import {
    EmblaCarousel,
    EmblaContainer,
    List,
    Item,
    ControlsContainer,
    Button,
    Icon,
    NavList,
    NavItem
} from './Grid.styled';

interface Item {
    id: string;
}

export interface Props {
    items: Item[];
    Component: React.ElementType;
    label: string;
}

const Grid: React.FC<Props> = props => {
    const { items, Component, label } = props;

    const [views, setViews] = useState<Item[][]>([[]]);

    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: true,
        align: 'start',
        inViewThreshold: 0.8
    });
    const [slidesInView, setSlidesInView] = useState<number[]>([]);

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
    const scrollTo = useCallback(
        (index: number) => emblaApi && emblaApi.scrollTo(index),
        [emblaApi]
    );

    const onSlidesInView = useCallback((emblaApi: EmblaCarouselType) => {
        setSlidesInView(emblaApi.slidesInView());
    }, []);

    useEffect(() => {
        if (!emblaApi) return;

        onSlidesInView(emblaApi);
        emblaApi.on('slidesInView', onSlidesInView);
    }, [emblaApi, onSlidesInView]);

    useEffect(() => {
        const itemsInView = 4;
        const viewCount = Math.ceil(items.length / itemsInView);
        const views: Item[][] = [];

        for (let index = 0; index < viewCount; index++) {
            views.push(items.slice(itemsInView * index, itemsInView * (index + 1)));
        }

        setViews(views);
    }, [items]);

    return (
        <>
            <EmblaCarousel ref={emblaRef}>
                <EmblaContainer>
                    {views.map((viewItems, index) => (
                        <List key={index}>
                            {viewItems.map(item => (
                                <Item key={item.id}>
                                    <Component {...item} />
                                </Item>
                            ))}
                        </List>
                    ))}
                </EmblaContainer>
            </EmblaCarousel>
            {slidesInView.length !== views.length && (
                <ControlsContainer aria-hidden="true">
                    <Button
                        $previous
                        onClick={() => scrollPrev()}
                        tabIndex={-1}
                        aria-label="Go to the previous slide"
                    >
                        <Icon />
                    </Button>
                    <nav aria-label={`${label}-grid-carousel nav`}>
                        <NavList>
                            {views.map((_, index) => (
                                <NavItem key={index}>
                                    <NavButton
                                        aria-label={`Go to slide ${index + 1}`}
                                        aria-current={slidesInView.includes(index)}
                                        isActive={slidesInView.includes(index)}
                                        onClick={() => scrollTo(index)}
                                        tabIndex={slidesInView.includes(index) ? -1 : 0}
                                    />
                                </NavItem>
                            ))}
                        </NavList>
                    </nav>
                    <Button
                        onClick={() => scrollNext()}
                        tabIndex={-1}
                        aria-label="Go to the next slide"
                    >
                        <Icon />
                    </Button>
                </ControlsContainer>
            )}
        </>
    );
};

export default Grid;
